import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { MatTreeModule } from '@angular/material/tree';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { SolEstimatePricingMode } from '../../models/enum';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { SolEstimateTableMaterialCostsComponent } from '../sol-estimate-table-rows/sol-estimate-table-material-costs/sol-estimate-table-material-costs.component';
import { SolEstimateTableLaborCostsComponent } from "../sol-estimate-table-rows/sol-estimate-table-labor-costs/sol-estimate-table-labor-costs.component";
import { SolEstimateTableSubcontractorsCostsComponent } from '../sol-estimate-table-rows/sol-estimate-table-subcontractors-costs/sol-estimate-table-subcontractors-costs.component';
import { SolEstimateTableMiscRiskTravelCostsComponent } from '../sol-estimate-table-rows/sol-estimate-table-misc-risk-travel-costs/sol-estimate-table-misc-risk-travel-costs.component';
import { SolEstimateTableTaxNotIncludedComponent } from "../sol-estimate-table-rows/sol-estimate-table-tax-not-included/sol-estimate-table-tax-not-included.component";
import { SolEstimateTableTaxMaterialRateComponent } from "../sol-estimate-table-rows/sol-estimate-table-tax-material-rate/sol-estimate-table-tax-material-rate.component";
import { SolEstimateTableTaxMaterialLaborRateComponent } from "../sol-estimate-table-rows/sol-estimate-table-tax-material-labor-rate/sol-estimate-table-tax-material-labor-rate.component";
import { SolEstimateTableTechnologyFeeComponent } from "../sol-estimate-table-rows/sol-estimate-table-technology-fee/sol-estimate-table-technology-fee.component";
import { SolEstimateTableSubTotalsComponent } from "../sol-estimate-table-rows/sol-estimate-table-sub-totals/sol-estimate-table-sub-totals.component";
import { SolEstimateFormGroup } from '../../models/sol-estimate-table/SolEstimateFormGroup';
import { SolEstimateTableMaterialCostsRowCollection } from '../../models/sol-estimate-table/SolEstimateTableMaterialCostsRowCollection';
import { SolEstimateTableRowBase } from '../../models/sol-estimate-table/SolEstimateTableRowBase';
import { SolEstimateTableLaborCosts } from '../../models/sol-estimate-table/SolEstimateTableLaborCosts';
import { SolEstimateTableSubContractorsCosts } from '../../models/sol-estimate-table/SolEstimateTableSubContractorsCosts';
import { SolEstimateTableMiscRiskTravelCosts } from '../../models/sol-estimate-table/SolEstimateTableMiscRiskTravelCosts';
import { SolEstimateTableSellAsComponent } from '../sol-estimate-table-rows/sol-estimate-table-sell-as/sol-estimate-table-sell-as.component';
import { SolEstimateTableSellAsGroup } from '../../models/sol-estimate-table/SolEstimateTableSellAsGroup';
import { SolEstimateTableTaxRate } from '../../models/sol-estimate-table/SolEstimateTableTaxRate';
import { TranslatePipe } from '@ngx-translate/core';
import { SolEstimateRiskDialogData } from '../../models/sol-estimate-table/SolEstimateRiskDialogData';
import { Subscription } from 'rxjs';
import { CommonDataService } from '../../services/common-data.service';
import { CollaborationState } from '../../models/dtos';
import { CONSTANTS } from '../../Constants/Constants';

@Component({
  selector: 'app-sol-estimate-table',
  standalone: true,
  imports: [
    MatTreeModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    CommonModule,
    TranslatePipe,
    MatExpansionModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonToggleModule,
    SolEstimateTableMaterialCostsComponent,
    SolEstimateTableLaborCostsComponent,
    SolEstimateTableSubcontractorsCostsComponent,
    SolEstimateTableMiscRiskTravelCostsComponent,
    SolEstimateTableTaxNotIncludedComponent,
    SolEstimateTableTaxMaterialRateComponent,
    SolEstimateTableTaxMaterialLaborRateComponent,
    SolEstimateTableTechnologyFeeComponent,
    SolEstimateTableSubTotalsComponent,
    SolEstimateTableSellAsComponent
],
  templateUrl: './sol-estimate-table.component.html',
  styleUrls: ['./sol-estimate-table.component.scss']
})
export class SolEstimateTableComponent implements OnInit, OnDestroy {
  
  isCollaborativeNotEdit: boolean = false;

  @Input() estimateTableFormGroup: FormGroup<SolEstimateFormGroup>;
  @Input() taxVisibility: { showTaxNotIncluded: boolean; showTaxMaterialRate: boolean; showTaxMaterialLaborRate: boolean } = {
    showTaxNotIncluded: false,
    showTaxMaterialRate: false,
    showTaxMaterialLaborRate: false
  };
  @Output() isMarkUpSelected: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  private estimateDataSubscription: Subscription;

  constructor(private readonly commonDataService: CommonDataService) {}

  ngOnInit() {
    if (!this.estimateTableFormGroup) {
      return;
    }

    this.updateCollaborativeState();
    this.estimateDataSubscription = this.commonDataService.estimateDataChanged$.subscribe(() => {
      this.updateCollaborativeState();
    });
  }

  private updateCollaborativeState(): void {
    this.isCollaborativeNotEdit = (this.commonDataService.isCollaborationEstimate()
      && this.commonDataService.getCollaborationState() !== CollaborationState.Edit);
  }

  ngOnDestroy(): void {
    this.estimateDataSubscription?.unsubscribe();
  }

  solEstimatePricingMode = SolEstimatePricingMode;

  get materialCostsFormGroup(): FormGroup<SolEstimateTableMaterialCostsRowCollection> | null {
    return this.estimateTableFormGroup?.controls.materialCosts ?? null;
  }
  
  get laborCostsFormGroup(): FormGroup<SolEstimateTableLaborCosts> | null {
    return this.estimateTableFormGroup?.controls.laborCosts ?? null;
  }

  get subContractorCostsFormGroup(): FormGroup<SolEstimateTableSubContractorsCosts> | null {
    return this.estimateTableFormGroup?.controls.subContractorCosts ?? null;
  }

  get miscRiskTravelCostsFormGroup(): FormGroup<SolEstimateTableMiscRiskTravelCosts> | null {
    return this.estimateTableFormGroup?.controls.miscRiskTravelCosts ?? null;
  }

  get taxNotIncludedFormGroup(): FormGroup<SolEstimateTableRowBase> | null {
    return this.estimateTableFormGroup?.controls.taxNotIncluded ?? null;
  }

  get taxMaterialRateFormGroup(): FormGroup<SolEstimateTableTaxRate> | null {
    return this.estimateTableFormGroup?.controls.taxMaterialRate ?? null;
  }

  get taxMaterialLaborRateFormGroup(): FormGroup<SolEstimateTableTaxRate> | null {
    return this.estimateTableFormGroup?.controls.taxMaterialLaborRate ?? null;
  }

  get technologyFeeFormGroup(): FormGroup<SolEstimateTableRowBase> | null {
    return this.estimateTableFormGroup?.controls.technologyFee ?? null;
  }

  get subTotalsFormGroup(): FormGroup<SolEstimateTableRowBase> | null {
    return this.estimateTableFormGroup?.controls.subTotals ?? null;
  }

  get sellAsFormGroup(): FormGroup<SolEstimateTableSellAsGroup> | null {
    return this.estimateTableFormGroup?.controls.sellAs ?? null;
  }

  get getMarkUpToggleValue(): boolean {  
    return this.estimateTableFormGroup.controls.markUpToggle.value;
  }

  get sellAsPricingModeValue(): SolEstimatePricingMode {
    return this.estimateTableFormGroup.controls.sellAs.controls.sellAsPricingMode.value ?? SolEstimatePricingMode.Line_Item;
  }

  get riskDialogRequiredData(): SolEstimateRiskDialogData {
    return {
      subtotal: this.subTotalsFormGroup.controls.cost.value,
      techFee: this.technologyFeeFormGroup.controls.cost.value,
      riskCost: CONSTANTS.ZERO
    };
  }

  get isMarkUpToggleDisabled(): boolean {
    return this.estimateTableFormGroup.controls.markUpToggle.disabled;
  }

  setIsMarkUpSelected() {
    this.isMarkUpSelected.emit(this.estimateTableFormGroup.controls.markUpToggle.value);
  }



}
